import { useState } from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { T, Icon, stitchOutlineBtn, stitchSectionLabel } from '../parts/stitch';
import TranscriptCard from '../parts/TranscriptCard';
import { useProjectData } from '../ProjectDataContext';
import { bevestig } from '../../../utils/bevestig';

/**
 * Meetings bij dit project — twee lagen:
 * - Gekoppeld: door een mens aangevinkt; telt mee en voedt de dossier-afleiding.
 * - Suggesties: klant-/projectnaam-matching in de titel (nooit projectnummer,
 *   dat staat er niet in) — pas na "koppel" wordt het een feit.
 */
function MeetingsPanel() {
  const navigate = useNavigate();
  const {
    id, transcripts, meetingSuggesties, loadMeetings, projectNumber,
  } = useProjectData();
  const [busyId, setBusyId] = useState(null);

  const koppel = async (meetingId) => {
    setBusyId(meetingId);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${id}/meetings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ meetingId, projectNumber }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      await loadMeetings();
    } catch (e) { toast.error(`Koppelen mislukt: ${e.message}`); }
    finally { setBusyId(null); }
  };

  const ontkoppel = async (meetingId) => {
    if (!(await bevestig({ titel: 'Meeting ontkoppelen van dit project?', tekst: 'De meeting zelf blijft bestaan; alleen de koppeling verdwijnt.', knop: 'Ontkoppelen' }))) return;
    setBusyId(meetingId);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${id}/meetings/${meetingId}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await loadMeetings();
    } catch (e) { toast.error(`Ontkoppelen mislukt: ${e.message}`); }
    finally { setBusyId(null); }
  };

  const rij = (meeting, gekoppeld) => (
    <Box key={meeting.id} sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TranscriptCard meeting={meeting} onClick={() => navigate(`/MeetingDashboard/${meeting.id}`)} />
      </Box>
      <Box component="span"
        onClick={() => busyId !== meeting.id && (gekoppeld ? ontkoppel(meeting.id) : koppel(meeting.id))}
        title={gekoppeld
          ? 'Ontkoppel — deze meeting hoort niet bij dit project'
          : 'Koppel aan dit project — telt dan mee en mag het dossier voeden'}
        sx={{ ...stitchOutlineBtn, alignSelf: 'center', px: 1, py: 0.6, opacity: busyId === meeting.id ? 0.5 : 1 }}>
        <Icon name={gekoppeld ? 'link_off' : 'add_link'} size={17} />
      </Box>
    </Box>
  );

  const { project } = useProjectData();
  const klantRaw = project?.fields?.KlantnaamString;
  const klantNaam = Array.isArray(klantRaw) ? (klantRaw[0] || '') : (klantRaw || '');

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography sx={{ ...stitchSectionLabel }}>
          Gekoppeld aan dit project ({transcripts.length})
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Box component="span"
          onClick={() => navigate(`/MeetingDashboard${klantNaam ? `?klant=${encodeURIComponent(klantNaam)}` : ''}`)}
          sx={{ ...stitchOutlineBtn, py: 0.4, fontSize: '0.75rem' }}>
          <Icon name="mic" size={15} /> Naar Meetings
        </Box>
      </Box>
      {transcripts.length === 0 ? (
        <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, display: 'block', mb: 2 }}>
          Nog geen meetings gekoppeld — vink hieronder aan welke opnames bij dit project horen.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          {transcripts.map((m) => rij(m, true))}
        </Box>
      )}

      <Typography sx={{ ...stitchSectionLabel, mb: 1 }}>
        Suggesties op klantnaam ({meetingSuggesties.length})
      </Typography>
      {meetingSuggesties.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Icon name="mic" size={40} sx={{ color: T.outlineVariant, display: 'block', mx: 'auto', mb: 1 }} />
          <Typography sx={{ fontFamily: T.fontBody, color: T.outline }}>Geen verdere meetings gevonden op klant- of projectnaam</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {meetingSuggesties.map((m) => rij(m, false))}
        </Box>
      )}
    </>
  );
}

export default MeetingsPanel;
